import React, { useState, useEffect } from 'react';
import { ProfileIcon } from './ProfileIcon';

interface UserAvatarProps {
  userId: string;
  username?: string;
  size?: number;
  className?: string;
  onClick?: () => void;
  /** Append as ?v= to bust the browser cache after a picture change. */
  cacheKey?: number;
}

/**
 * User avatar component that displays a profile picture or default icon.
 * Automatically handles loading states and fallback to default icon.
 * Uses the by-user endpoint which resolves the correct file extension server-side.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  userId,
  username,
  size = 32,
  className = '',
  onClick,
  cacheKey,
}) => {
  const [imageError, setImageError] = useState(false);

  // Use the by-user endpoint which resolves the correct extension server-side.
  // Append ?v=<cacheKey> when provided so a changed picture is re-fetched immediately.
  const profilePictureUrl = userId
    ? `/api/users/profile/picture/by-user/${userId}${cacheKey ? `?v=${cacheKey}` : ''}`
    : null;

  const handleImageError = () => {
    setImageError(true);
  };

  // Reset error state when userId or cacheKey changes (new upload should retry)
  useEffect(() => {
    setImageError(false);
  }, [userId, cacheKey]);

  return (
    <div
      className={`user-avatar ${onClick ? 'user-avatar--clickable' : ''} ${className}`}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        cursor: onClick ? 'pointer' : 'default',
      }}
      title={username || 'User'}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {profilePictureUrl && !imageError ? (
        <img
          src={profilePictureUrl}
          alt={username ? `${username}'s profile` : 'User profile'}
          className="user-avatar-image"
          onError={handleImageError}
        />
      ) : (
        <div className="user-avatar-placeholder">
          <ProfileIcon size={size * 0.6} className="user-avatar-icon" />
        </div>
      )}
    </div>
  );
};
